import React, { useEffect, useState } from "react"
import { useMutation, useQuery } from "urql"

import { useFeatureFlags } from "../../contexts/FeatureFlagContext"
import { usePractice } from "../../contexts/PracticeContext"
import { useToast } from "../../contexts/ToastContext"
import AddCreditCardModal from "../../pages/ManagePaymentMethods/AddCreditCardModal"
import { DETACH_PAYMENT_METHOD_PRACTICE_MUTATION } from "../../pages/ManagePaymentMethods/ManagePaymentMethods"
import { ARCHIVE_INTAKE_FORM_SUBMISSION_MUTATION } from "../../utils/intakeFormSubmissionMutations"
import { DEFAULT_PAYMENT_METHOD_QUERY, RECHECK_APPOINTMENTS_AFTER_CARD_SAVE_MUTATION } from "../../utils/mutations"
import { capitalize, formatPhone } from "../../utils/utils"
import PaymentProviderWrapper from "../payments/PaymentProviderWrapper"
import AppointmentHistory from "../pro_portal/calendar/AppointmentHistory"
import UpcomingAppointments from "../pro_portal/calendar/UpcomingAppointments"

import ArchiveIntakeFormSubmissionModal from "./ArchiveIntakeFormSubmissionModal"
import { Button } from "./Buttons"
import ClientNotes from "./ClientNotes"
import { Label } from "./Inputs"
import IntakeFormListSection from "./IntakeFormListSection"
import IntakeFormSelectModal, { MODE_SEND } from "./IntakeFormSelectModal"
import { Flyout, Divider } from "./Layout"
import { ButtonLink } from "./Links"
import { AnimatedModal } from "./Modal"
import Typography from "./Typography"

const CLIENT_INTAKE_SUBMISSIONS_QUERY = `
  query ClientIntakeSubmissions($clientId: ID!) {
    client(clientId: $clientId) {
      id
      intakeSubmissions {
        intakeFormId
        intakeFormName
        status
        completedAt
      }
    }
  }
`

const SEND_INTAKE_FORMS_MUTATION = `
  mutation SendIntakeForms($clientId: ID!, $intakeFormIds: [ID!]!) {
    sendIntakeForms(clientId: $clientId, intakeFormIds: $intakeFormIds) {
      result
      errors
    }
  }
`

const SEND_CLIENT_PHONE_REMINDER_MUTATION = `
  mutation SendClientPhoneReminder($clientId: ID!) {
    sendClientPhoneReminder(clientId: $clientId) {
      result
      errors
    }
  }
`

export default function ClientFlyout({ client, closeFlyout, visible }) {
  const [paymentMethod, setPaymentMethod] = useState(client?.defaultPaymentMethod)
  const [creditCardModalOpen, setCreditCardModalOpen] = useState(false)
  const [showRemoveCardModal, setShowRemoveCardModal] = useState(false)
  const [intakeSelectModalOpen, setIntakeSelectModalOpen] = useState(false)
  const [archiveTargetForm, setArchiveTargetForm] = useState(null)

  const { practice } = usePractice()
  const { showToast } = useToast()
  const featureFlags = useFeatureFlags()
  const multipleIntakeFormsEnabled = featureFlags?.multipleIntakeFormsEnabled !== false

  const [{ data, fetching }, reexecuteQuery] = useQuery({
    query: DEFAULT_PAYMENT_METHOD_QUERY,
    variables: {
      clientId: client?.id,
      practiceId: practice.id
    },
    requestPolicy: "cache-and-network"
  })

  const [{ fetching: detachingPaymentMethod }, detachPaymentMethodPractice] = useMutation(
    DETACH_PAYMENT_METHOD_PRACTICE_MUTATION
  )
  const [, recheckAppointmentsAfterCardSave] = useMutation(RECHECK_APPOINTMENTS_AFTER_CARD_SAVE_MUTATION)
  const [{ fetching: sendingPhoneReminder }, sendClientPhoneReminder] = useMutation(SEND_CLIENT_PHONE_REMINDER_MUTATION)

  const [{ data: intakeData }, reexecuteIntake] = useQuery({
    query: CLIENT_INTAKE_SUBMISSIONS_QUERY,
    variables: { clientId: client?.id },
    requestPolicy: "cache-and-network",
    pause: !client?.id || !visible
  })

  const [, sendIntakeForms] = useMutation(SEND_INTAKE_FORMS_MUTATION)
  const [, archiveIntakeFormSubmission] = useMutation(ARCHIVE_INTAKE_FORM_SUBMISSION_MUTATION)

  const intakeSubmissions = intakeData?.client?.intakeSubmissions ?? []
  const completedFormIds = intakeSubmissions.filter((s) => s.status === "completed").map((s) => s.intakeFormId)

  const handleSendForms = (selectedForms) => {
    const formIds =
      Array.isArray(selectedForms) && selectedForms.length > 0 && typeof selectedForms[0] === "object"
        ? selectedForms.map((f) => f.id)
        : selectedForms
    sendIntakeForms({ clientId: client.id, intakeFormIds: formIds }).then((result) => {
      if (result.error) {
        showToast({
          content: multipleIntakeFormsEnabled ? "Error sending intake forms" : "Error sending intake form",
          type: "error"
        })
        return
      }
      const payload = result.data?.sendIntakeForms
      if (payload?.result === "error") {
        showToast({ content: payload.errors?.join(", ") ?? "Error", type: "error" })
        return
      }
      showToast(multipleIntakeFormsEnabled ? "Intake forms sent" : "Intake form sent")
      reexecuteIntake({ requestPolicy: "network-only" })
    })
  }

  const handleFormAction = (actionId, form) => {
    if (actionId === "view" && form?.id) {
      window.location.href = `/portal/clients/${client.id}/intake_form?formId=${form.id}`
      return
    }
    if (actionId === "resend") {
      handleSendForms([form.id])
      return
    }
    if (actionId === "remove") {
      setArchiveTargetForm(form)
    }
  }

  const handleArchiveConfirm = async () => {
    if (!archiveTargetForm?.id) return

    const result = await archiveIntakeFormSubmission({
      clientId: client.id,
      intakeFormId: archiveTargetForm.id
    })

    if (result.error) {
      showToast({ content: "Error deleting intake response", type: "error" })
      return
    }

    const payload = result.data?.archiveIntakeFormSubmission
    if (payload?.result === "error") {
      showToast({ content: payload.errors?.join(", ") ?? "Error deleting intake response", type: "error" })
      return
    }

    showToast("Intake response deleted")
    setArchiveTargetForm(null)
    reexecuteIntake({ requestPolicy: "network-only" })
  }

  useEffect(() => {
    if (client) {
      setPaymentMethod(null)
    }
  }, [client])

  useEffect(() => {
    if (data?.defaultPaymentMethodPractice) {
      setPaymentMethod(data.defaultPaymentMethodPractice)
    }
  }, [data])

  return (
    <Flyout
      header="Client Details"
      visible={visible}
      closeFlyout={closeFlyout}
      footer={
        <div className="flex items-center justify-end gap-4">
          <Button type="tertiary" onClick={closeFlyout}>
            Close
          </Button>
          <ButtonLink href={`/portal/clients/${client?.id}`}>View</ButtonLink>
        </div>
      }>
      {client && (
        <div>
          <div>
            <Label className="mb-1">Full name</Label>
            <div>{client.name ? client.name : client.firstName}</div>
          </div>

          <>
            <Divider />
            <Label className="mb-1 mt-6">Phone</Label>
            {formatPhone(client.phone) ? (
              <Typography className="mb-6" variant="body">
                {formatPhone(client.phone)}
              </Typography>
            ) : (
              <div className="mb-6 flex items-center justify-between gap-4">
                <Typography variant="body">No phone on file</Typography>
                {client.email && (
                  <button
                    type="button"
                    className="shrink-0 font-bold text-teal hover:underline disabled:opacity-50"
                    disabled={sendingPhoneReminder}
                    onClick={() => {
                      sendClientPhoneReminder({ clientId: client.id }).then((res) => {
                        const payload = res.data?.sendClientPhoneReminder
                        if (payload?.result === "success") {
                          showToast({ content: "Reminder sent", type: "success" })
                        } else {
                          showToast({
                            content: payload?.errors?.[0] || "Could not send reminder",
                            type: "error"
                          })
                        }
                      })
                    }}>
                    {sendingPhoneReminder ? "Sending…" : "Remind to add phone"}
                  </button>
                )}
              </div>
            )}
          </>
          {client.email && (
            <>
              <Divider />
              <Label className="mb-1 mt-6">Email</Label>
              <Typography className="mb-6" variant="body">
                {client.email}
              </Typography>
            </>
          )}

          <Divider className="mb-4" />
          {practice.clientNotesEnabled && (
            <>
              <ClientNotes client={client} />
              <Divider className="mb-4" />
            </>
          )}

          <Label className="mb-1 mt-6">Card on file</Label>

          {!practice.hasPaymentProvider ? (
            <a className="font-bold text-teal hover:underline" href={`/portal/settings/banking`}>
              Click here to connect to a payment provider
            </a>
          ) : fetching ? (
            <img className="loading" src="/loading-spinner.png" width="40px" />
          ) : paymentMethod ? (
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <Typography variant="body">
                {capitalize(paymentMethod.brand)} ending in {paymentMethod.last4} • Expires {paymentMethod.expMonth}/
                {paymentMethod.expYear}
              </Typography>
              <div className="flex shrink-0 gap-4">
                <button
                  className="font-bold text-red hover:underline"
                  type="button"
                  onClick={() => setShowRemoveCardModal(true)}>
                  Remove card
                </button>
                <button
                  className="font-bold text-teal hover:underline"
                  type="button"
                  onClick={() => {
                    setCreditCardModalOpen(true)
                  }}>
                  Change
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <Typography variant="body">None</Typography>
              <button
                className="font-bold text-teal hover:underline"
                onClick={() => {
                  setCreditCardModalOpen(true)
                }}>
                Add
              </button>
            </div>
          )}

          {practice.hasPaymentProvider && creditCardModalOpen && (
            <PaymentProviderWrapper practice={practice} client={client}>
              <AddCreditCardModal
                headerLabel={"Add a credit card"}
                open={creditCardModalOpen}
                closeModal={() => setCreditCardModalOpen(false)}
                practice={practice}
                onSave={() => {
                  reexecuteQuery()
                  if (practice.paymentProvider === "square") {
                    recheckAppointmentsAfterCardSave({ clientId: client.id, practiceId: practice.id })
                  }
                }}
              />
            </PaymentProviderWrapper>
          )}

          {showRemoveCardModal && client && (
            <AnimatedModal
              visible={showRemoveCardModal}
              header="Remove card on file"
              hideModal={() => setShowRemoveCardModal(false)}
              actionButtonCopy="Yes, remove card"
              actionButtonType="destructive"
              showFooter={true}
              saveDisabled={detachingPaymentMethod}
              onSave={() => {
                detachPaymentMethodPractice({ clientId: client.id }).then((res) => {
                  if (res.error) {
                    showToast({ content: "There was an error deleting the payment method", type: "error" })
                    setShowRemoveCardModal(false)
                    return
                  }
                  if (res.data?.detachPaymentMethodPractice?.result === "success") {
                    showToast({ content: "Payment method deleted successfully", type: "success" })
                    setPaymentMethod(null)
                    reexecuteQuery({ requestPolicy: "network-only" })
                  } else {
                    const errs = res.data?.detachPaymentMethodPractice?.errors
                    showToast({
                      content: errs?.length ? errs.join(", ") : "There was an error deleting the payment method",
                      type: "error"
                    })
                    console.error(res) // eslint-disable-line no-console
                  }
                  setShowRemoveCardModal(false)
                })
              }}>
              <p className="text-sm text-gray-dark">
                This removes the saved card for this client from your payment provider. Future charges may require a new
                card until one is added again. This cannot be undone from here except by adding a new card.
              </p>
            </AnimatedModal>
          )}

          <Divider className="mb-6 mt-4" />
          <UpcomingAppointments client={client} />
          <Divider />
          <AppointmentHistory client={client} limit={2} />
          {practice.clientNotesEnabled && (
            <>
              <Divider className="my-4" />
              <IntakeFormListSection
                forms={intakeSubmissions.map((s) => ({
                  id: s.intakeFormId,
                  name: s.intakeFormName,
                  status: s.status,
                  completedAt: s.completedAt
                }))}
                actionLabel="Send a form"
                onAction={() => setIntakeSelectModalOpen(true)}
                onFormAction={handleFormAction}
                showActionButton={multipleIntakeFormsEnabled}
                multipleIntakeFormsEnabled={multipleIntakeFormsEnabled}
                actionIdsByStatus={{
                  not_sent: [],
                  sent: ["view", "resend"],
                  completed: ["view", "remove"]
                }}
              />
              <ArchiveIntakeFormSubmissionModal
                visible={!!archiveTargetForm}
                formName={archiveTargetForm?.name}
                onClose={() => setArchiveTargetForm(null)}
                onConfirm={handleArchiveConfirm}
              />
              {multipleIntakeFormsEnabled && (
                <IntakeFormSelectModal
                  visible={intakeSelectModalOpen}
                  onClose={() => setIntakeSelectModalOpen(false)}
                  onSubmit={handleSendForms}
                  completedFormIds={completedFormIds}
                  mode={MODE_SEND}
                />
              )}
            </>
          )}
        </div>
      )}
    </Flyout>
  )
}
